import { useState } from "react";
import copyAreaImage from "../../../../assets/images/tools/copy-area.png";
import {
  Alert,
  Snackbar,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

interface AreaData {
  id: number;
  value: number;
}

export function AreaCalculatorTool() {
  const [inputText, setInputText] = useState("");
  const [areas, setAreas] = useState<AreaData[]>([]);
  const [totalArea, setTotalArea] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const getText = (text: string): [number[], number[]] | null => {
    if (!text) {
      return null;
    }
    const contentsTextArray = text.replaceAll("：", ":").split(/\n/, 300);
    const areaNumInt: number[] = [];
    const areaNumDec: number[] = [];
    

    let j = 0;
    for (const rowData of contentsTextArray) {
      const areaValue = rowData.match(/[０-９|0-9]+:[０-９|0-9]+/);

      if (areaValue != null) {
        let convertedValue = areaValue[0].replace(/[０-９]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
        });
        convertedValue = convertedValue.replace(":", ".");
        const splitAreaValue = convertedValue.split(".");

        areaNumInt[j] = splitAreaValue[0] ? Number(splitAreaValue[0]) : 0;
        areaNumDec[j] = splitAreaValue[1] ? Number(splitAreaValue[1]) : 0;
        j++;
      }
    }
    return [areaNumInt, areaNumDec];
  };

  const calculateArea = () => {
    try {
      const result = getText(inputText);
      if (!result) {
        setError("有効な入力テキストを入力してください。");
        setAreas([]);
        setTotalArea(null);
        return;
      }

      const [areaNumInt, areaNumDec] = result;
      if (areaNumInt.length === 0) {
        setError("面積データが見つかりませんでした。");
        setAreas([]);
        setTotalArea(null);
        return;
      }

      const calculatedAreas: AreaData[] = areaNumInt.map((int, index) => ({
        id: index + 1,
        value: Number(`${int}.${areaNumDec[index] || 0}`),
      }));

      const total = calculatedAreas.reduce((sum, area) => sum + area.value, 0);
      
      setAreas(calculatedAreas);
      setTotalArea(total);
      setError(null);

      // クリップボードにコピー
      navigator.clipboard.writeText(total.toFixed(2));
      setOpenSnackbar(true);
    } catch (err) {
      setError("計算中にエラーが発生しました。");
      setAreas([]);
      setTotalArea(null);
    }
  };

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        登記簿PDFから面積部分のテキストをコピーして入力してください。
      </Typography>
      <Box sx={{ mb: 2 }}>
        <img 
          src={copyAreaImage} 
          alt="登記簿の面積コピー例" 
          style={{ 
            maxWidth: '100%', 
            height: 'auto',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }} 
        />
      </Box>
      <TextField
        fullWidth
        multiline
        rows={6}
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        placeholder="ここにテキストを貼り付けてください"
        variant="outlined"
        sx={{ mb: 2 }}
      />
      <Button
        variant="contained"
        onClick={calculateArea}
        sx={{ mb: 2 }}
      >
        計算する
      </Button>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {areas.length > 0 && (
        <>
        {totalArea !== null && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">
              合計面積: {totalArea.toFixed(2)} ㎡
            </Typography>
          </Paper>
        )}
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No.</TableCell>
                  <TableCell align="right">面積（㎡）</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {areas.map((area) => (
                  <TableRow key={area.id}>
                    <TableCell>{area.id}</TableCell>
                    <TableCell align="right">
                      {area.value.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          合計面積をクリップボードにコピーしました
        </Alert>
      </Snackbar>
    </Box>
  );
}
